import { supabase } from '../lib/supabase';

export const userService = {
  // Get all users with optional filtering and pagination
  async getUsers(options = {}) {
    try {
      const { 
        page = 1, 
        limit = 10, 
        search = '', 
        role = null, 
        status = null 
      } = options;
      
      let query = supabase?.from('user_profiles')?.select('*', { count: 'exact' });

      // Apply search filter
      if (search) {
        query = query?.or(`full_name.ilike.%${search}%,email.ilike.%${search}%`);
      }

      // Apply role filter
      if (role) {
        query = query?.eq('role', role);
      }

      // Apply status filter
      if (status !== null) {
        query = query?.eq('is_active', status);
      }

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query?.range(from, to);

      // Order by created date (newest first)
      query = query?.order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      return {
        data: data || [],
        total: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users');
    }
  },

  // Get user by ID
  async getUserById(id) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', id)?.single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Failed to fetch user');
    }
  },

  // Create new user
  async createUser(userData) {
    try {
      // First create the auth user
      const { data: authData, error: authError } = await supabase?.auth?.admin?.createUser({
        email: userData?.email,
        password: userData?.password,
        email_confirm: true,
        user_metadata: {
          full_name: userData?.full_name
        }
      });

      if (authError) {
        throw authError;
      }

      // Then create the user profile
      const { data, error } = await supabase?.from('user_profiles')?.insert([{
          id: authData?.user?.id,
          email: userData?.email,
          full_name: userData?.full_name,
          role: userData?.role || 'member',
          membership_tier: userData?.membership_tier || 'free',
          is_active: userData?.is_active !== undefined ? userData?.is_active : true
        }])?.select()?.single();

      if (error) {
        // If profile creation fails, try to delete the auth user
        await supabase?.auth?.admin?.deleteUser(authData?.user?.id);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error creating user:', error);
      if (error?.message?.includes('already registered')) {
        throw new Error('A user with this email already exists');
      }
      throw new Error('Failed to create user');
    }
  },

  // Update user
  async updateUser(id, userData) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: userData?.full_name,
          email: userData?.email,
          role: userData?.role,
          membership_tier: userData?.membership_tier,
          is_active: userData?.is_active,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user');
    }
  },

  // Delete user
  async deleteUser(id) {
    try {
      // Delete the user profile first
      const { error: profileError } = await supabase?.from('user_profiles')?.delete()?.eq('id', id);

      if (profileError) {
        throw profileError;
      }

      // Then delete the auth user
      const { error: authError } = await supabase?.auth?.admin?.deleteUser(id);
      
      if (authError) {
        console.error('Failed to delete auth user:', authError);
        // Profile is already deleted, so we'll consider this a success
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  },

  // Bulk update user status
  async bulkUpdateStatus(userIds, isActive) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ 
          is_active: isActive,
          updated_at: new Date()?.toISOString()
        })?.in('id', userIds)?.select();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error bulk updating users:', error);
      throw new Error('Failed to bulk update users');
    }
  },

  // Get user statistics
  async getUserStats() {
    try {
      const [totalResult, activeResult, adminResult, premiumResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true }),
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.eq('is_active', true),
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.eq('role', 'admin'),
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.in('membership_tier', ['premium', 'gold'])
      ]);

      return {
        totalUsers: totalResult?.count || 0,
        activeUsers: activeResult?.count || 0,
        adminUsers: adminResult?.count || 0,
        premiumUsers: premiumResult?.count || 0
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw new Error('Failed to fetch user statistics');
    }
  },

  // Get user activities with collectible details
  async getUserActivities(userId, limit = 10) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data, error } = await supabase
        ?.from('user_activities')
        ?.select(`
          id,
          title,
          description,
          activity_type,
          created_at,
          metadata,
          collectible_id,
          collectibles (
            id,
            name,
            brand,
            model,
            primary_image_url,
            current_value,
            collectible_type
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        throw error;
      }

      // Format activities for frontend consumption
      const formattedActivities = (data || [])?.map(activity => ({
        id: activity?.id,
        title: activity?.title,
        description: activity?.description,
        type: activity?.activity_type,
        date: activity?.created_at,
        metadata: activity?.metadata || {},
        collectible: activity?.collectibles ? {
          id: activity?.collectibles?.id,
          name: activity?.collectibles?.name,
          brand: activity?.collectibles?.brand,
          model: activity?.collectibles?.model,
          imageUrl: activity?.collectibles?.primary_image_url,
          value: activity?.collectibles?.current_value,
          type: activity?.collectibles?.collectible_type
        } : null
      }));

      return {
        data: formattedActivities,
        total: formattedActivities?.length
      };
    } catch (error) {
      console.error('Error fetching user activities:', error);
      throw new Error('Failed to fetch user activities');
    }
  }
};

// Export the userService object as UserService for consistent naming
export const UserService = userService;